import {
  FocusStyleManager,
  HotkeysProvider,
  Icon,
  ProgressBar,
} from '@blueprintjs/core';
import { BOSToaster } from 'Components/Generic/BOSToaster';

// Support Components
import { HELP_EMAIL } from 'Config';
import ABRCodeData from 'Data/ABRCodeData';

// Data
import AppData from 'Data/AppData';
import { AlertStateProvider } from 'Data/context/Alerts';
import { AuthProvider, useAuthContext } from 'Data/context/Auth';
import { ForceUpdateProvider } from 'Data/context/ForceUpdater';
import { GlobalStateProvider } from 'Data/context/GlobalState';
import { OrgStateProvider } from 'Data/context/Organizations';
import { UserStateProvider } from 'Data/context/Users';
import { VendorStateProvider } from 'Data/context/Vendors';
import { ViewportSizerProvider } from 'Data/context/ViewportSizer';

// Site Dialog
import SiteDialogData from 'Data/SiteDialogData';
import DataManager from 'Lib/DataManager';
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import { createHashRouter, RouterProvider } from 'react-router';

import styles from 'Styles/styles.module.css';

import SiteRoutes from './routes';

let DM = new DataManager();

// Enable behavior which hides focus styles during mouse interaction
FocusStyleManager.onlyShowFocusOnTabs();

const AppStateContext = createContext({
  loading: false,
});
AppStateContext.displayName = 'AppStateContext';

const App = () => {
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const { handleAuthChange } = useAuthContext();

  useEffect(() => {
    DM.add(
      AppData.registerListener('REQUEST_SERVER_ERROR', requestErrorHandler),
      AppData,
    );
    DM.add(
      AppData.registerListener('REQUEST_GATEWAY_ERROR', requestErrorHandler),
      AppData,
    );
    DM.add(
      AppData.registerListener('REQUEST_SUBMISSION ERROR', requestErrorHandler),
      AppData,
    );
    DM.add(
      AppData.registerListener('REQUEST_RESOURCE_MISSING', requestErrorHandler),
      AppData,
    );
    DM.add(
      AppData.registerListener(
        'REQUEST_AUTHORIZATION_DENIED',
        authErrorHandler,
      ),
      AppData,
    );

    AppData.getUserConfig().then(() => {
      setLoading(false);
      handleAuthChange();
      if (AppData.isLoggedIn()) {
        ABRCodeData.populateABRList();
      }
    });

    return () => {
      DM.clear();
    };
  }, []);

  const authErrorHandler = (authErrorPayload) => {
    if (
      authErrorPayload?.text !== 'Not logged in' &&
      authErrorPayload?.text !== 'No session active.' &&
      authErrorPayload?.message !== 'Not logged in' &&
      authErrorPayload?.message !== 'No session active.'
    ) {
      requestErrorHandler(authErrorPayload);
    } else {
      AppData.logout();
    }
  };

  const requestErrorHandler = (payload) => {
    let lineBreak = '%0D%0A';
    let emailBody =
      `${lineBreak}---------------------------------------------------------` +
      `${lineBreak}(Please provide details as to what you were doing leading up to the error above.)${lineBreak}${lineBreak}Error from ${
        payload.url
      }${lineBreak}${
        payload.debugObject?.message || payload.message
      }${lineBreak}${
        payload.debugObject.errors?.length
          ? payload.debugObject.errors.join(', ')
          : ''
      }`;
    SiteDialogData.open(
      'Request Error',
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          whiteSpace: 'pre',
          overflow: 'auto',
          wordWrap: 'break-word',
        }}>
        <div>{`Error from ${payload.url}`}</div>
        {!payload.debugObject?.message && payload.message}
        {payload.htmlText?.title && (
          <>
            <div
              dangerouslySetInnerHTML={{ __html: `${payload.htmlText.title}` }}
            />
            <div dangerouslySetInnerHTML={{ __html: payload.htmlText.body }} />
          </>
        )}
        {payload?.debugObject && (
          <>
            {payload.debugObject.message}
            {payload.debugObject.errors?.length &&
              payload.debugObject.errors.map((err, index) => (
                <div key={index} style={{ marginLeft: 8 }}>
                  {err}
                </div>
              ))}
          </>
        )}
        {payload.stack && (
          <div style={{ paddingTop: 8, fontStyle: 'italic' }}>
            {payload.stack}
          </div>
        )}
      </div>,
      [
        <a
          key={1}
          className={`${styles.primary_button_bold} bp5-button`}
          style={{ marginLeft: 12 }}
          href={`mailto:${HELP_EMAIL}?subject=BOS Error&body=${emailBody}`}
          onMouseDown={(e) => e.preventDefault()}>
          <Icon icon={'envelope'} size={20} />
          <span>{'Email the Devs'}</span>
        </a>,
      ],
      'issue',
      true,
    );
  };

  let router = createHashRouter([
    {
      path: '*',
      element: <SiteRoutes />,
    },
  ]);

  return (
    <AppStateContext.Provider value={{ loading }}>
      <ForceUpdateProvider forceParentUpdate={forceUpdate}>
        <RouterProvider router={router} fallbackElement={<ProgressBar />} />
      </ForceUpdateProvider>
    </AppStateContext.Provider>
  );
};

createRoot(document.getElementById('content')).render(
  <AuthProvider>
    <GlobalStateProvider>
      <UserStateProvider>
        <OrgStateProvider>
          <VendorStateProvider>
            <AlertStateProvider>
              <HotkeysProvider>
                <ViewportSizerProvider>
                  <App />
                </ViewportSizerProvider>
              </HotkeysProvider>
              <BOSToaster />
            </AlertStateProvider>
          </VendorStateProvider>
        </OrgStateProvider>
      </UserStateProvider>
    </GlobalStateProvider>
  </AuthProvider>,
);
export const useAppStateContext = () => useContext(AppStateContext);
